import React, { useState } from 'react';
import { TextField, Popover, Paper, InputAdornment } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import { capitalizeFirstLetter } from '../utils/capitalize';
import Icon from './shared/Icon';
import { t } from 'i18next';
import IconButton from './shared/IconButton';

interface CustomDateInputProps {
    label?: string;
    onChange?: (date: Date | null) => void;
    defaultDate?: Date | null;
    required?: boolean;
    minDate?: Date;
    maxDate?: Date;
}

const CustomDateInput: React.FC<CustomDateInputProps> = ({
    label = 'Sélectionner une date',
    onChange,
    defaultDate,
    required = false,
    minDate,
    maxDate,
}) => {
    const [selectedDate, setSelectedDate] = useState(defaultDate);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDateChange = (value: Dayjs | null) => {
        const newDate = value ? value.toDate() : null;
        setSelectedDate(newDate);
        onChange && onChange(newDate);
        handleClose();
    };

    return (
        <div className=" pad-5px">
            <TextField
                label={capitalizeFirstLetter(label)}
                value={selectedDate ? dayjs(selectedDate).format('DD/MM/YYYY') : ''}
                variant="outlined"
                fullWidth
                onClick={handleOpen}
                size="small"
                slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: true },
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                {!required && selectedDate && (
                                    <IconButton
                                        iconName="close"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDateChange(null);
                                        }}
                                        size="small"
                                        iconProps={{ fontSize: 'small' }}
                                        title={capitalizeFirstLetter(t('clear'))}
                                    />
                                )}
                                <IconButton
                                    iconName="calendarToday"
                                    onClick={handleOpen}
                                    title={capitalizeFirstLetter(t('open'))}
                                    size="small"
                                    iconProps={{ fontSize: 'small' }}
                                />
                            </InputAdornment>
                        ),
                    },
                }}
                autoComplete="off"
                required={required}
            />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: { borderRadius: '10px' },
                    },
                }}
            >
                <DateCalendar
                    onChange={handleDateChange}
                    value={selectedDate ? dayjs(selectedDate) : null}
                    displayWeekNumber
                    minDate={minDate ? dayjs(minDate) : undefined}
                    maxDate={maxDate ? dayjs(maxDate) : undefined}
                />
            </Popover>
        </div>
    );
};

export default CustomDateInput;
